import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { auth, onAuthStateChanged } from "../services/firebaseApi";
import {
  ArrowUpRight,
  Activity,
  Cpu,
  Droplets,
  Thermometer,
  Wind,
  BarChart3,
  Bell,
  Shield,
  Leaf,
  Wifi,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Quote,
} from "lucide-react";
import { PageShell, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import SafeImage from "../components/SafeImage";
import { contentService } from "../services/contentService";

function FaqRow({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <div className={`rounded-2xl border transition-all ${open ? "border-accent bg-cream" : "border-border bg-card"}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-6 p-6 text-left" type="button">
        <span className="font-display text-xl">{q}</span>
        <ChevronDown className={`h-5 w-5 text-accent shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-6 text-muted-foreground leading-relaxed">{a}</div>}
    </div>
  );
}

function Slider() {
  const [index, setIndex] = useState(0);
  const [slides, setSlides] = useState([]);
  // Intentionally no autoplay — change only on user action (arrows/dots).

  const current = slides[index] || null;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const dbStories = await contentService.listStories();
        if (!mounted) return;
        const mapped = dbStories.map((s, idx) => ({
          eyebrow: s.eyebrow || `Field note · ${String(idx + 1).padStart(2, "0")}`,
          title: s.title || "Untitled story",
          caption: s.summary || "",
          image: s.imageUrl || "",
        }));
        setSlides(mapped);
        setIndex(0);
      } catch (_e) {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (!current) {
    return (
      <div className="rounded-[2rem] bg-card border border-border p-8 text-sm text-muted-foreground shadow-soft">
        No stories available in database.
      </div>
    );
  }

  return (
    <div className="rounded-[2rem] bg-card border border-border overflow-hidden shadow-soft">
      <div className="grid lg:grid-cols-2">
        <div className="relative min-h-[320px] bg-[var(--gradient-forest)]">
          <SafeImage
            src={current.image}
            alt={current.eyebrow}
            className="absolute inset-0 h-full w-full object-cover opacity-85"
            fallbackLabel="Stories"
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_30%)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
          <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-card/90 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.22em]">
            <Quote className="h-3 w-3 text-accent" /> {current.eyebrow}
          </div>
        </div>
        <div className="p-8 md:p-12 flex flex-col justify-between gap-8">
          <div>
            <h3 className="font-display text-3xl md:text-4xl text-balance">{current.title}</h3>
            <p className="mt-5 text-muted-foreground leading-relaxed">{current.caption}</p>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-1.5">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setIndex(idx)}
                  aria-label={`Slide ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === index ? "w-8 bg-accent" : "w-4 bg-border hover:bg-muted-foreground"}`}
                  type="button"
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setIndex((v) => (v - 1 + slides.length) % slides.length)} className="h-11 w-11 rounded-full border border-border hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all flex items-center justify-center" type="button">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => setIndex((v) => (v + 1) % slides.length)} className="h-11 w-11 rounded-full border border-border hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all flex items-center justify-center" type="button">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [user, setUser] = useState(undefined);
  const [activeDevices, setActiveDevices] = useState(0);
  const [registeredUsers, setRegisteredUsers] = useState(0);
  const [sensorsPerNode, setSensorsPerNode] = useState(3);
  const [homeModules, setHomeModules] = useState([]);
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const iconMap = { Activity, BarChart3, Bell, Cpu };
    let mounted = true;
    (async () => {
      try {
        const items = await contentService.listHomeModules();
        if (!mounted) return;
        setHomeModules(
          (items || []).map((m) => ({
            ...m,
            icon: iconMap[m.icon] || Activity,
          }))
        );
      } catch (_e) {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const dbFaqs = await contentService.listFaqs();
        if (!mounted) return;
        setFaqs(
          dbFaqs.map((f) => ({
            q: f.question || "",
            a: f.answer || "",
          }))
        );
      } catch (_e) {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const stats = await contentService.getPublicStats();
        setActiveDevices(stats.activeDevices);
        setRegisteredUsers(stats.registeredUsers);
        setSensorsPerNode(stats.sensorsPerNode);
        localStorage.setItem("sf_public_activeDevices", String(stats.activeDevices));
        localStorage.setItem("sf_public_registeredUsers", String(stats.registeredUsers));
      } catch (error) {
        // If API is unreachable, keep the last known values.
        const cachedDevices = Number(localStorage.getItem("sf_public_activeDevices") || "0");
        const cachedUsers = Number(localStorage.getItem("sf_public_registeredUsers") || "0");
        if (cachedDevices) setActiveDevices(cachedDevices);
        if (cachedUsers) setRegisteredUsers(cachedUsers);
        console.error("Public stats fetch error:", error.message);
      }
    };

    fetchData();
  }, []);

  const stats = [
    { value: `${activeDevices || 0}`, label: "Devices" },
    { value: `${registeredUsers || 0}`, label: "Accounts" },
    { value: `${sensorsPerNode}`, label: "Sensors" },
    { value: "2.4 GHz", label: "Board Wi-Fi" },
  ];

  return (
    <PageShell>
      <PageSection variant="hero" className="relative !px-6 !py-0">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 items-center pt-8 pb-12 sm:pb-24">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-cream px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              <Leaf className="h-3 w-3 text-accent" />
              Noida · small plots
            </div>
            <h1 className="mt-6 font-display text-[clamp(2.6rem,6vw,5.5rem)] leading-[0.95] text-balance">
              Check the soil <span className="italic text-accent">before</span>
              <br />you water.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl text-pretty leading-relaxed">
              A board in the plot sends soil moisture, air temperature, and humidity to this app. From the same screen you can turn a pump on or off, or leave a moisture rule to do it.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={user ? "/dashboard" : "/login"}>
                <Button variant="accent" size="lg" className="group">
                  {user ? "Open dashboard" : "Login"}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                </Button>
              </Link>
              <Link to="/benefits">
                <Button variant="outline" size="lg">See benefits</Button>
              </Link>
            </div>
            <dl className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 max-w-xl">
              {stats.map((s) => (
                <div key={s.label} className="border-l-2 border-accent pl-4">
                  <dt className="font-display text-3xl">{s.value}</dt>
                  <dd className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-elevated aspect-[4/5] bg-[var(--gradient-forest)]">
              <div className="absolute inset-0">
                <SafeImage
                  src="/Images/hero-farm.jpg"
                  alt="Farm hero"
                  className="h-full w-full object-cover opacity-80"
                  fallbackLabel="Smart Farming"
                />
              </div>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_30%)]" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
              <div className="absolute top-4 right-4 rounded-full bg-card/90 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-foreground">
                Sample
              </div>
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl p-4 text-cream border border-white/10 bg-black/20 backdrop-blur">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cream/70">Example card</div>
                    <div className="font-display text-2xl mt-0.5">Not a live plot</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div><Thermometer className="h-4 w-4 mx-auto text-accent" /><div className="font-display text-xl mt-1">30°</div><div className="text-[9px] uppercase tracking-wider text-cream/60">temp</div></div>
                  <div><Droplets className="h-4 w-4 mx-auto text-accent" /><div className="font-display text-xl mt-1">54%</div><div className="text-[9px] uppercase tracking-wider text-cream/60">humid</div></div>
                  <div><Wind className="h-4 w-4 mx-auto text-accent" /><div className="font-display text-xl mt-1">70%</div><div className="text-[9px] uppercase tracking-wider text-cream/60">soil</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection tone="marquee" variant="marquee" className="overflow-hidden">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-cream/90 to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-cream/90 to-transparent pointer-events-none" />
          {(() => {
            const items = ["Soil moisture", "Air temperature", "Humidity", "Wi-Fi telemetry", "Threshold alerts", "Historical trends"];
            return (
              <div className="w-full overflow-hidden">
                <div className="flex w-max animate-[marquee_30s_linear_infinite] will-change-transform">
                  {[0, 1].map((dup) => (
                    <div key={dup} className="flex shrink-0 items-center gap-8 sm:gap-12 pr-8 sm:pr-12">
                      {items.map((t, i) => (
                        <span key={`${dup}-${t}-${i}`} className="font-display text-2xl sm:text-3xl text-foreground/60 flex shrink-0 items-center gap-8 sm:gap-12 whitespace-nowrap">
                          {t}
                          <span className="text-accent">✦</span>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </PageSection>

      <PageSection>
        <div className="mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-3">What you get after sign-in</div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-balance">Readings, history, alerts, and the pump.</h2>
            </div>
            <p className="lg:col-span-5 text-muted-foreground text-lg leading-relaxed">
              The cards below are the jobs on the dashboard. A weather forecast and a camera are not among them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {homeModules.map((m, i) => (
              <div key={m.title} className="group relative rounded-3xl bg-card border border-border p-8 hover:shadow-elevated transition-all duration-500 hover:-translate-y-1 overflow-hidden">
                <div className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-muted-foreground font-mono">0{i + 1} · {m.tag}</div>
                <m.icon className="h-10 w-10 text-accent" />
                <h3 className="mt-6 font-display text-3xl">{m.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{m.desc}</p>
                <div className="absolute bottom-0 left-0 h-1 w-0 bg-accent group-hover:w-full transition-all duration-700" />
              </div>
            ))}
          </div>
          {!homeModules.length ? (
            <div className="mt-6 text-sm text-muted-foreground">No module content available in database.</div>
          ) : null}
        </div>
      </PageSection>

      <PageSection tone="secondary">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-square rounded-[2rem] overflow-hidden bg-[var(--gradient-warm)]">
            <SafeImage
              src="/Images/soil-hands.jpg" 
              alt="Soil and hands"
              className="absolute inset-0 h-full w-full object-cover opacity-90"
              fallbackLabel="Soil"
            />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_30%)]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-3">The pump</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-balance">The pump sits next to the reading.</h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              If the board has a relay, the dashboard can start the pump, stop it, run it for a set number of minutes, or start it when soil moisture drops under a number you set.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Wifi, title: "2.4 GHz only", desc: "The ESP8266 board cannot join a 5 GHz network. You send the network name from Settings." },
                { icon: Shield, title: "Your account", desc: "The dashboard requires sign-in. A device is stored on the account that added it." },
              ].map((f) => (
                <div key={f.title} className="rounded-2xl border border-border bg-card p-5">
                  <f.icon className="h-5 w-5 text-accent mb-3" />
                  <div className="font-display text-xl">{f.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection tone="primary" variant="cta" className="relative overflow-hidden">
        <div className="mx-auto max-w-4xl text-center text-cream">
          <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-4">Noida</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-balance">Want a board on a plot?</h2>
          <p className="mt-6 text-lg text-cream/80 max-w-2xl mx-auto">
            Write with the plot size and whether you already have a pump. Aryan and Chetan reply from Noida.
          </p>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/contact"><Button variant="accent" size="lg">Contact us</Button></Link>
            <Link to="/pricing"><Button variant="cream" size="lg">See pricing</Button></Link>
          </div>
        </div>
      </PageSection>

      <PageSection tone="secondary-light">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-3">From the hardware</div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-balance max-w-2xl">What a node does today.</h2>
            </div>
            <p className="text-muted-foreground max-w-sm">Notes on the board and the app. These are not customer stories.</p>
          </div>
          <Slider />
        </div>
      </PageSection>

      <PageSection>
        <div className="mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.22em] text-accent mb-3">FAQ</div>
            <h2 className="font-display text-5xl">Common questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => <FaqRow key={i} q={f.q} a={f.a} defaultOpen={i === 0} />)}
          </div>
          {!faqs.length ? <div className="text-sm text-muted-foreground mt-4">No FAQs available in database.</div> : null}
        </div>
      </PageSection>

    </PageShell>
  );
}

